import { BadgeCheck, User, CalendarDays, Clock } from "lucide-react";
import api from "../../Utils/api";
import { useEffect, useState } from "react";

export default function UserShift({ shifts }) {
  const [serverTime, setServerTime] = useState(null);

  const getServerTime = async () => {
    const res = await api.get("/user/server_time");
    setServerTime(res.data.serverTime);
  };

  useEffect(() => {
    getServerTime();
  }, []);

  // Don't render until serverTime is loaded
  if (!serverTime) return <div>Loading...</div>;

  // Sort shifts by startTime ascending
  const sortedShifts = [...shifts].sort(
    (a, b) => new Date(a.startTime) - new Date(b.startTime)
  );

  // Split into upcoming and past shifts
  const upcomingShifts = sortedShifts.filter(
    (shift) => new Date(shift.startTime) > new Date(serverTime)
  );
  const pastShifts = sortedShifts.filter(
    (shift) => new Date(shift.startTime) < new Date(serverTime)
  );

  // Helper to render a shift card
  const renderShiftCard = (shift) => {
    const start = new Date(shift.startTime);
    const end = new Date(shift.endTime);

    const dateHeader = start.toLocaleDateString("en-US", {
      weekday: "short",
      day: "2-digit",
      month: "short",
      year: "numeric",
    });

    const timeRange = `${start.toISOString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    })} – ${end.toISOString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    })}`;

    const locationName = shift.locationID?.name || "Unknown location";

    return (
      <div
        key={shift._id}
        className="bg-gray-50 p-4 relative w-full mb-4 rounded shadow"
      >
        <h3 className="text-gray-800 font-semibold mb-2">{dateHeader}</h3>
        <p className="text-gray-700 font-semibold text-sm mb-2">{timeRange}</p>
        <p className="text-gray-700 text-sm mb-2">{locationName}</p>
        {/* <span className="text-sm text-gray-400">30m meal break</span> */}
        <StatusTag status={shift.status} />
      </div>
    );
  };

  return (
    <div className="p-6">
      {/* Work Details Card */}
      <section className="bg-white mb-16 -mt-6">
        <h2 className="text-gray-800 font-semibold text-3xl mb-4">Shifts</h2>
        <hr className="w-full mb-2 mt-2" />

        <div className="text-xl font-semibold my-4">
          Upcoming shifts:
          <span className="px-2 py-1 bg-gray-200 ml-4 rounded-md">
            {upcomingShifts.length}
          </span>
        </div>
        {upcomingShifts.map(renderShiftCard)}

        <div className="text-xl text-gray-800 font-semibold mt-16 mb-4">
          Timesheets:{" "}
          <span className="px-2 py-1 bg-gray-200 ml-4 rounded-md">
            {pastShifts.length}
          </span>
        </div>
        {pastShifts.map(renderShiftCard)}
      </section>
    </div>
  );
}

const statusColors = {
  created: {
    background: "bg-gray-100", // Tailwind sky blue 200
    text: "text-gray-800",
  },
  started: {
    background: "bg-red-100", // Tailwind sky 200
    text: "text-red-800",
  },
  pending: {
    background: "bg-orange-300", // Tailwind orange 300 (deep orange)
    text: "text-orange-900",
  },
  approved: {
    background: "bg-green-100", // Tailwind green 200
    text: "text-green-800",
  },
};

function StatusTag({ status }) {
  const lowerStatus = status.toLowerCase();
  const colors = statusColors[lowerStatus] || {
    background: "bg-gray-200",
    text: "text-gray-700",
  };

  return (
    <span
      className={`inline-block px-3 py-1 text-xs rounded-full font-medium ${colors.background} ${colors.text} select-none`}
      aria-label={`Status: ${status}`}
      role="status"
    >
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
}
